import React from "react";
import { useAuth } from "store/useAuth";
import { getDashboardScope } from "utils/dashboard-permissions";
import { useDashboardSummary } from "hooks/useDashboardSummary";
import MetricCard from "./MetricCard";

/**
 * Componente contenedor de Métricas / KPIs del Dashboard.
 * Gobernado por la capa de permisos por rol (`getDashboardScope`):
 *   - Supervisor: Ve métricas globales (ALL_CENTERS)
 *   - Director: Ve métricas de su centro (CURRENT_CENTER)
 *   - Operador: Retorna null (NO realiza request de métricas ni renderiza la sección)
 */
export const DashboardMetrics: React.FC = () => {
  const { currentUser } = useAuth();
  const scope = getDashboardScope(currentUser);

  const { data, isLoading, isError, refetch } = useDashboardSummary(scope);

  // Operador u otros roles sin métricas: No renderizar
  if (!scope.showMetrics || scope.scope === "NONE") {
    return null;
  }

  // Error state
  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800 flex items-center justify-between gap-4 mb-6">
        <span>No pudimos cargar el resumen de métricas.</span>
        <button
          onClick={() => refetch()}
          className="px-3 py-1 bg-white border border-red-300 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors shrink-0"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const metrics = data?.metrics;
  const scopeLabel =
    scope.scope === "ALL_CENTERS"
      ? "Consolidado global (Todos los centros)"
      : `Métricas del Centro (${data?.scope?.centerName ?? "Tu centro"})`;

  return (
    <section aria-label="Métricas principales" className="mb-8 space-y-3">
      {/* Header del Scope de Métricas */}
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400">
          Resumen General · <span className="text-blue-600 font-semibold">{scopeLabel}</span>
        </h2>
        <span className="text-xs text-gray-400">
          {scope.roleName}
        </span>
      </div>

      {/* Grilla 4 Columnas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Familias Beneficiarias"
          metric={metrics?.familiasBeneficiarias}
          loading={isLoading}
          variant="positive"
          tooltipText="Madres únicas con estado En Programa"
          icon={
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          }
        />

        <MetricCard
          title="Niños/as en DIF"
          metric={metrics?.difChildren}
          loading={isLoading}
          variant="neutral"
          tooltipText="Niños y niñas con tratamiento activo en Desarrollo Infantil en Familia"
          icon={
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
          }
        />

        <MetricCard
          title="Madres en EMB"
          metric={metrics?.embMothers}
          loading={isLoading}
          variant="positive"
          tooltipText="Madres con tratamiento activo en el Programa de Embarazadas"
          icon={
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          }
        />

        <MetricCard
          title="Madres en OFI"
          metric={metrics?.ofiMothers}
          loading={isLoading}
          variant="neutral"
          tooltipText="Madres con tratamiento activo en el Programa de Oficios"
          icon={
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          }
        />
      </div>
    </section>
  );
};

export default DashboardMetrics;
